import { FileText } from "@phosphor-icons/react";
import LegalPageShell from "@/components/LegalPageShell";
import { useI18n } from "@/context/I18nContext";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

export default function TermsOfUse() {
    const { t } = useI18n();
    return (
        <LegalPageShell
            testId="terms-page"
            kicker={t("legal.terms_kicker")}
            title={t("legal.terms_title")}
            sub={t("legal.terms_sub")}
            updated="February 2026"
            icon={<FileText size={28} weight="fill" />}
        >
            <p>
                Welcome to PawLink. By creating an account or using our Smart QR pet ID service you
                agree to the terms below. Please read them carefully — they exist to keep you, your
                pet, and the finders who help you all safe.
            </p>

            <h2>1. What PawLink is</h2>
            <p>
                PawLink provides Smart QR-based digital identity for pets. Owners create profiles,
                receive real-time alerts when their pet is scanned, and can activate a Lost Mode
                during emergencies. PawLink is a communication tool between owners and finders; it
                is not a veterinary, medical, or emergency-response service.
            </p>

            <h2>2. Your account</h2>
            <ul>
                <li>You must be at least <strong>13 years old</strong> (or the legal age in your country) to create a PawLink account.</li>
                <li>You are responsible for the accuracy of the information you provide about yourself and your pet.</li>
                <li>Keep your login credentials confidential; you are responsible for activity that happens under your account.</li>
                <li>One account per person; sharing accounts commercially is not allowed.</li>
            </ul>

            <h2>3. Acceptable use</h2>
            <p>You agree not to use PawLink to:</p>
            <ul>
                <li>Upload false, misleading, or fraudulent pet information.</li>
                <li>Impersonate another person or claim ownership of a pet that is not yours.</li>
                <li>Send harassing, unlawful, or spam messages to finders or owners.</li>
                <li>Attempt to reverse-engineer, disrupt, or overload the service.</li>
            </ul>
            <p>
                Violations may result in account suspension or termination, and in serious cases
                referral to law enforcement.
            </p>

            <h2>4. Content you provide</h2>
            <p>
                You retain ownership of the content you upload (pet photos, names, messages, voice
                recordings). By uploading, you grant PawLink a worldwide, royalty-free license to
                store and display that content solely to operate the service — showing your pet&rsquo;s
                profile to a finder who scans the tag, sending notifications, and running backups.
            </p>

            <h2>5. PawLink Protection subscription</h2>
            <ul>
                <li><strong>PawLink Protection is a digital software subscription.</strong> PawLink does not sell, ship, deliver, or supply any physical product. The Smart QR is a digital feature generated by our software and linked to your pet&rsquo;s online profile; users may independently download and print their own QR code for personal use.</li>
                <li>The Protection plan is billed <strong>9.90 CHF / month or 79.00 CHF / year</strong> via Paddle.</li>
                <li>You may cancel at any time from the Protection page; access continues until the end of the billing period.</li>
                <li>Refunds outside of applicable consumer laws are handled at our discretion; please contact support with your request.</li>
                <li>Prices may change; existing subscribers will be notified before any renewal at a new price.</li>
            </ul>

            <h2>6. Scans and finder communication</h2>
            <p>
                When a finder scans your Smart QR they can contact you by phone or SMS using the
                number you have chosen to publish. Finders may also leave you an optional message
                and their location. PawLink does not verify the identity of finders; use good
                judgment before meeting anyone in person to recover your pet.
            </p>

            <h2>7. Availability and support</h2>
            <p>
                We work hard to keep PawLink up 24/7, but we do not guarantee uninterrupted service.
                Planned maintenance, network outages, or third-party provider issues may briefly
                affect availability. Support requests are answered on business days.
            </p>

            <h2>8. Termination</h2>
            <p>
                You may delete your account at any time from the Account page. We may suspend or
                terminate accounts that breach these terms, with notice where reasonably possible.
                Upon termination, your pet profiles and scan history are permanently removed.
            </p>

            <h2>9. Liability</h2>
            <p>
                PawLink is provided <em>as is</em>. To the fullest extent permitted by law, PawLink,
                its founders, and employees are not liable for indirect, incidental, or consequential
                damages arising from use of the service, including but not limited to lost pets,
                missed alerts, or inaccurate location data.
            </p>

            <h2>10. Changes to these terms</h2>
            <p>
                We may update these terms occasionally. Material changes will be announced by email
                or in-app notification at least 15 days before they take effect. Continued use after
                changes take effect means you accept them.
            </p>

            <h2>11. Contact</h2>
            <p>
                Questions about these terms? Email <a href={supportMailto({ subject: "PawLink Support Request — Terms" })}>{SUPPORT_EMAIL}</a>
                {" "}or open a ticket from <a href="/support">Contact Support</a>.
            </p>
        </LegalPageShell>
    );
}
